<template>
  <div>
    <!-- Filtros Básicos Sempre Visíveis -->
    <v-card flat class="mb-4">
      <v-card-text class="pb-2">
        <v-row>
          <v-col md="4">
            <v-text-field
              background-color="grey lighten-4"
              hide-details="auto"
              type="date"
              solo
              :dense="false"
              flat
              v-model="searchParams.date_end"
              label="Data Final"
              @change="select(1)"
            />
          </v-col>
          <v-col md="4">
            <app-text-search-field
              label="Pesquisar por produtos"
              @input="handleSearchInput($event)"
              @filter="showAdvancedFilters = !showAdvancedFilters"
              :loading="isLoading"
            />
          </v-col>

          <v-col md="4">
            <v-menu
              v-model="columnMenu"
              :close-on-content-click="false"
              min-width="400"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  block
                  elevation="0"
                  class="pa-0 ma-0"
                  style="box-shadow: none !important; min-height: 46px;"
                >
                  Colunas
                  <v-icon right color="secondary">mdi-view-column</v-icon>
                </v-btn>
              </template>

              <v-card flat>
                <v-card-text
                  class="pa-0"
                  style="max-height: 400px; overflow-y: auto;"
                >
                  <v-list dense>
                    <v-list-item-group multiple>
                      <v-list-item
                        v-for="column in columnOptions"
                        :key="column.value"
                        @click="toggleColumn(column.value)"
                        class="px-4"
                      >
                        <v-list-item-action>
                          <v-checkbox
                            :input-value="visibleColumns.includes(column.value)"
                            color="primary"
                            hide-details
                            @click.stop="toggleColumn(column.value)"
                          />
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            {{ column.text }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-3">
                  <v-btn small color="primary" @click="selectAllColumns">
                    <v-icon left small>mdi-check-all</v-icon>
                  </v-btn>
                  <v-btn small outlined color="error" @click="clearAllColumns">
                    <v-icon left small>mdi-close-box-multiple</v-icon>
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn small color="primary" @click="resetDefaultColumns">
                    <v-icon left small>mdi-refresh</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Filtros Avançados - Ocultos por padrão -->
    <v-expand-transition>
      <v-card v-show="showAdvancedFilters" flat class="mb-4 grey lighten-4">
        <v-card-text class="pt-4 pb-2">
          <v-row>
            <v-col md="4" class="d-flex align-end">
              <company-select
                chips
                clearable
                v-model="searchParams.company_id"
                :item-value="(val) => val.id"
                :itemText="(val) => val.short_name"
                dense
                multiple
                style="width: 100%"
                @change="select(1)"
              />
            </v-col>
            <v-col md="4" class="d-flex align-end">
              <CategoriesSearch
                dense
                style="width: 100%"
                @input="handleRelationshipInput($event, 'category_id')"
              />
            </v-col>
            <v-col md="4" class="d-flex align-end">
              <BrandsSearch
                dense
                style="width: 100%"
                @input="handleRelationshipInput($event, 'brand_id')"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-expand-transition>

    <!-- Cards de Resumo -->

    <v-row v-if="products.data && products.data.length > 0">
      <v-col md="3">
        <v-card color="primary" elevation="0" dark>
          <v-card-text class="d-flex align-center justify-space-between">
            <div>
              Faturamento Total
              <br />
              <b class="text-h5">{{ $format.decimal(grandTotal || 0) }}</b>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col md="3">
        <v-card color="grey lighten-4" elevation="0">
          <v-card-text class="d-flex align-center justify-space-between">
            <div>
              Faturamento Médio <span class='caption'>(Soma)</span>
              <br />
              <b class="text-h5">{{ $format.decimal(grandAverage || 0) }}</b>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col md="3">
        <v-card color="grey lighten-4" elevation="0">
          <v-card-text class="d-flex align-center justify-space-between">
            <div>
              Lucro Projetado Total <span class='caption'>(Soma)</span>
              <br />
              <b class="text-h5">{{
                $format.decimal(grandProjectedTotalProfit || 0)
              }}</b>

              <span class="text-body-2 font-weight-medium text--secondary">
                ({{ formatPercentage(grandProjectedTotalProfitPercent) }})
              </span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col md="3">
        <v-card color="grey lighten-4" elevation="0">
          <v-card-text class="d-flex align-center justify-space-between">
            <div>
              Lucro Projetado Médio <span class='caption'>(Soma)</span>
              <br />
              <b class="text-h5">{{
                $format.decimal(grandProjectedAverageProfit || 0)
              }}</b>

              <span class="text-body-2 font-weight-medium text--secondary">
                ({{ formatPercentage(grandProjectedAverageProfitPercent) }})
              </span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Gráficos de Análise -->
    <v-row class="mt-6" v-if="products.data && products.data.length > 0">
      <!-- Gráfico de Lucro Real vs Projetada -->
      <v-col md="12">
        <v-card flat color="grey lighten-4" style="height: 100%;">
          <v-card-title class="text-subtitle-1">
            Lucro Real vs Projetada (Todos os produtos da página)
          </v-card-title>
          <v-card-text>
            <apexchart
              type="bar"
              height="300"
              :options="marginComparisonOptions"
              :series="marginComparisonSeries"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Gráfico de Análise 3D -->
      <v-col md="12">
        <v-card flat color="grey lighten-4" style="height: 100%;">
          <v-card-title class="text-subtitle-1">
            Análise 3D: Faturamento x Quantidade x % Lucro
          </v-card-title>
          <v-card-text>
            <apexchart
              type="bubble"
              height="300"
              :options="bubbleOptions"
              :series="bubbleSeries"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabela de Dados -->
    <v-row class="mt-6" v-if="products.data && products.data.length > 0">
      <v-col md="12">
        <v-card flat color="grey lighten-4">
          <v-card-title class="d-flex justify-space-between mb-4 pt-5">
            <span class="text-subtitle-1"> Análise de Preços e Margens</span>

            <div class="d-flex gap-4">
              <v-chip-group
                class="mr-8"
                v-model="searchParams.orderBy"
                mandatory
                @change="select(1)"
              >
                <v-chip filter outlined value="total_quantity">
                  Quantidade
                </v-chip>
                <v-chip filter outlined value="total_value">
                  Faturamento
                </v-chip>
                <v-chip filter outlined value="actual_average_profit">
                  Lucro Médio
                </v-chip>

                <v-chip filter outlined value="variant_profit_percent">
                  % Config.
                </v-chip>
                <v-chip filter outlined value="product_code"> Código </v-chip>
              </v-chip-group>

              <v-chip-group
                class="mr-8"
                v-model="searchParams.orderDirection"
                mandatory
                @change="select(1)"
              >
                <v-chip filter outlined value="desc"> Z-A </v-chip>
                <v-chip filter outlined value="asc"> A-Z </v-chip>
              </v-chip-group>

              <v-btn color="success" size="large" @click="handleExport">
                Exportar para Excel
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="filteredHeaders"
              :items="productsWithCalculatedFields"
              hide-default-footer
              :items-per-page="-1"
              class="table-striped grey lighten-4"
              flat
              style="background-color: #f5f5f5;"
            >
              <template v-slot:item.product_code="{ item }">
                <span>
                  {{ item.product_code }}
                </span>
              </template>

              <template v-slot:item.product_name="{ item }">
                <span class="font-weight-medium">
                  {{ item.product_name }}
                </span>
              </template>

              <template v-slot:item.variant_profit_percent="{ item }">
                <span>
                  {{ formatPercentage(item.variant_profit_percent) }}
                </span>
              </template>

              <template v-slot:item.total_quantity="{ item }">
                <span>
                  {{ formatNumber(item.total_quantity) }}
                </span>
              </template>

              <template v-slot:item.total_value="{ item }">
                <span>{{ formatCurrency(item.total_value) }}</span>
              </template>

              <template v-slot:item.actual_average_profit="{ item }">
                <span>
                  {{ formatCurrency(item.actual_average_profit) }}
                </span>
              </template>

              <template v-slot:item.actual_total_profit="{ item }">
                <span>
                  {{ formatCurrency(item.actual_total_profit) }}
                </span>
              </template>

              <template v-slot:item.actual_total_profit_percent="{ item }">
                <span>
                  {{ formatPercentage(item.actual_total_profit_percent) }}
                </span>
              </template>

              <template v-slot:item.average_total_value="{ item }">
                <span>
                  {{ formatCurrency(item.average_total_value) }}
                </span>
              </template>

              <template v-slot:item.projected_total_profit="{ item }">
                <span>
                  {{ formatCurrency(item.projected_total_profit) }}
                </span>
              </template>

              <template v-slot:item.projected_average_profit="{ item }">
                <span>
                  {{ formatCurrency(item.projected_average_profit) }}
                </span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Mensagem quando não há resultados -->
    <v-row class="text-center mt-16">
      <v-col cols="12" v-if="products.data && products.data.length <= 0">
        <p>Sem nenhum resultado para sua pesquisa...</p>
      </v-col>
    </v-row>

    <!-- Paginação -->
    <v-row v-if="products.links">
      <v-col>
        <app-pagination @click="select($event)" :data="products" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import CompanySelect from "@/components/company/ui/CompanySelect.vue";
import BrandsSearch from "@/components/brands/ui/BrandsSearch.vue";
import CategoriesSearch from "@/components/categories/ui/CategoriesSearch.vue";
import VueApexCharts from "vue-apexcharts";

import { format } from "date-fns";

export default {
  components: {
    CompanySelect,
    BrandsSearch,
    CategoriesSearch,
    apexchart: VueApexCharts,
  },

  data() {
    return {
      headers: [
        { text: "Código", value: "product_code", align: "center" },
        { text: "Produto", value: "product_name", align: "start" },
        {
          text: "% Lucro Config.",
          value: "variant_profit_percent",
          align: "end",
        },
        { text: "Qtd Vendida", value: "total_quantity", align: "end" },
        {
          text: "Faturamento Médio",
          value: "average_total_value",
          align: "end",
        },
        {
          text: "Lucro Real (Médio)",
          value: "actual_average_profit",
          align: "end",
        },
        {
          text: "Lucro Projetada (Médio)",
          value: "projected_average_profit",
          align: "end",
        },
        { text: "Faturamento Total", value: "total_value", align: "end" },
        {
          text: "Lucro Real (Total)",
          value: "actual_total_profit",
          align: "end",
        },
        {
          text: "% Lucro Real",
          value: "actual_total_profit_percent",
          align: "end",
        },
        {
          text: "Lucro Projetada (Total)",
          value: "projected_total_profit",
          align: "end",
        },
      ],

      // ===== CONTROLE DE COLUNAS =====
      columnOptions: [
        { text: "Código", value: "product_code" },
        { text: "Produto", value: "product_name" },
        { text: "% Lucro Config.", value: "variant_profit_percent" },
        { text: "Qtd Vendida", value: "total_quantity" },
        { text: "Faturamento Médio", value: "average_total_value" },
        { text: "Lucro Real (Médio)", value: "actual_average_profit" },
        {
          text: "Lucro Projetada (Médio)",
          value: "projected_average_profit",
        },
        { text: "Faturamento Total", value: "total_value" },
        { text: "Lucro Real (Total)", value: "actual_total_profit" },
        { text: "% Lucro Real", value: "actual_total_profit_percent" },
        {
          text: "Lucro Projetada (Total)",
          value: "projected_total_profit",
        },
      ],

      // Colunas visíveis por padrão (as mais essenciais)
      visibleColumns: [
        "product_code",
        "product_name",
        "variant_profit_percent",
        "actual_average_profit",
        "projected_average_profit",
        "actual_total_profit_percent",
      ],

      // Colunas padrão para reset
      defaultColumns: [
        "product_code",
        "product_name",
        "variant_profit_percent",
        "actual_average_profit",
        "projected_average_profit",
        "actual_total_profit_percent",
      ],

      // ===== ESTADO DA UI =====
      columnMenu: false,
      showAdvancedFilters: false,
      isLoading: false,

      // ===== DADOS DA API =====
      products: {
        data: [],
      },

      // ===== PARÂMETROS DE BUSCA =====
      searchParams: {
        orderBy: "total_quantity",
        orderDirection: "desc",
        company_id: [],
        date_end: format(new Date(), "yyyy-MM-dd"),
        status: ["sale"],
        search: null,
        category_id: null,
        brand_id: null,
        page: 1,
      },
    };
  },

  mounted() {
    this.select(1);
  },

  computed: {
    // ===== TABELA E FILTROS =====
    filteredHeaders() {
      return this.headers.filter((header) =>
        this.visibleColumns.includes(header.value)
      );
    },

    productsWithCalculatedFields() {
      if (!this.hasValidData()) {
        return [];
      }

      return this.products.data.map((item) => {
        const actualTotalProfit =
          parseFloat(item.actual_average_profit) *
          parseFloat(item.total_quantity);
        const totalValue = parseFloat(item.total_value);
        const actualTotalProfitPercent =
          totalValue > 0 ? (actualTotalProfit / totalValue) * 100 : 0;
        const averageTotalValue =
          parseFloat(item.total_quantity) > 0
            ? totalValue / parseFloat(item.total_quantity)
            : 0;
        const projectedTotalProfit =
          totalValue * (parseFloat(item.variant_profit_percent) / 100);
        const projectedAverageProfit =
          averageTotalValue * (parseFloat(item.variant_profit_percent) / 100);

        return {
          ...item,
          actual_total_profit: actualTotalProfit,
          actual_total_profit_percent: actualTotalProfitPercent,
          average_total_value: averageTotalValue,
          projected_total_profit: projectedTotalProfit,
          projected_average_profit: projectedAverageProfit,
        };
      });
    },

    // ===== CARDS DE RESUMO =====
    grandTotal() {
      return this.getGrandValue("grand_total");
    },

    grandAverage() {
      return this.getGrandValue("grand_average");
    },

    grandProjectedTotalProfit() {
      return this.getGrandValue("grand_projected_total_profit");
    },

    grandProjectedAverageProfit() {
      return this.getGrandValue("grand_projected_average_profit");
    },

    grandProjectedTotalProfitPercent() {
      const total = this.grandTotal;
      const projectedProfit = this.grandProjectedTotalProfit;
      return total > 0 ? (projectedProfit / total) * 100 : 0;
    },

    grandProjectedAverageProfitPercent() {
      const average = this.grandAverage;
      const projectedAverageProfit = this.grandProjectedAverageProfit;
      return average > 0 ? (projectedAverageProfit / average) * 100 : 0;
    },

    // ===== GRÁFICOS - DADOS =====
    marginComparisonSeries() {
      if (
        !this.productsWithCalculatedFields ||
        this.productsWithCalculatedFields.length === 0
      ) {
        return [
          { name: "Lucro Real", data: [] },
          { name: "Lucro Projetada", data: [] },
        ];
      }
      // Usar todos os produtos da página (até 30)
      return [
        {
          name: "Lucro Real",
          data: this.productsWithCalculatedFields.map((item) =>
            this.formatNumber(item.actual_total_profit)
          ),
        },
        {
          name: "Lucro Projetada",
          data: this.productsWithCalculatedFields.map((item) =>
            this.formatNumber(item.projected_total_profit)
          ),
        },
      ];
    },

    bubbleSeries() {
      if (
        !this.productsWithCalculatedFields ||
        this.productsWithCalculatedFields.length === 0
      ) {
        return [{ name: "Produtos", data: [] }];
      }

      // Encontrar os valores min e max de % Lucro para normalização do tamanho
      const margins = this.productsWithCalculatedFields.map((item) =>
        Number(item.actual_total_profit_percent)
      );
      const minMargin = Math.min(...margins);
      const maxMargin = Math.max(...margins);
      const marginRange = maxMargin - minMargin;

      return [
        {
          name: "Produtos",
          data: this.productsWithCalculatedFields.map((item) => {
            const margin = Number(item.actual_total_profit_percent);

            const normalizedSize =
              marginRange > 0
                ? 10 + ((margin - minMargin) / marginRange) * 40
                : 25;

            return {
              x: Number(item.total_value),
              y: Number(item.total_quantity),
              z: Math.max(normalizedSize, 8),
              product: item,
            };
          }),
        },
      ];
    },

    // ===== GRÁFICOS - CONFIGURAÇÕES =====

    marginComparisonOptions() {
      if (
        !this.productsWithCalculatedFields ||
        this.productsWithCalculatedFields.length === 0
      ) {
        return {
          chart: { type: "bar", height: 400, toolbar: { show: false } },
          xaxis: { categories: [] },
        };
      }

      return {
        chart: {
          type: "bar",
          height: 400,
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "85%",
            endingShape: "rounded",
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
        },
        xaxis: {
          categories: this.productsWithCalculatedFields.map(
            (item) => item.product_code
          ),
          labels: {
            rotate: -45,
            style: {
              fontSize: "10px",
            },
          },
        },
        yaxis: {
          title: {
            text: "Lucro (R$)",
          },
          labels: {
            formatter: (val) => this.formatCurrency(val),
          },
        },
        fill: {
          opacity: 1,
        },
        tooltip: {
          y: {
            formatter: (val) => this.formatCurrency(val),
          },
        },
        colors: ["#008FFB", "#00E396"],
      };
    },

    bubbleOptions() {
      return {
        chart: {
          height: 350,
          type: "bubble",
          zoom: {
            enabled: true,
            type: "xy",
          },
          toolbar: { show: false },
        },
        dataLabels: {
          enabled: false,
        },
        fill: {
          opacity: 0.7,
        },
        plotOptions: {
          bubble: {
            minBubbleRadius: 8,
            maxBubbleRadius: 35,
          },
        },
        xaxis: {
          title: {
            text: "Faturamento Total (R$)",
          },
          labels: {
            formatter: (val) => `R$ ${Number(val).toLocaleString("pt-BR")}`,
          },
        },
        yaxis: {
          title: {
            text: "Quantidade Vendida",
          },
          labels: {
            formatter: (val) => `${Number(val).toFixed(0)}`,
          },
        },
        tooltip: {
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            if (
              !this.productsWithCalculatedFields ||
              dataPointIndex >= this.productsWithCalculatedFields.length
            ) {
              return `
                <div class="pa-3" style="background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
                  <div style="text-align: center; color: #666; font-size: 12px;">
                    Dados não disponíveis
                  </div>
                </div>
              `;
            }

            const product = this.productsWithCalculatedFields[dataPointIndex];
            const profitDifference =
              Number(product.actual_total_profit_percent) -
              Number(product.variant_profit_percent);
            const profitStatus = profitDifference >= 0 ? "success" : "warning";
            const profitColor = profitDifference >= 0 ? "#4CAF50" : "#FF9800";
            const profitIcon = profitDifference >= 0 ? "↗" : "↘";

            return `
              <div class="pa-3" style="background: white; border-radius: 12px; box-shadow: 0 4px 16px rgba(0,0,0,0.12); max-width: 280px; font-family: Arial, sans-serif;">
                <!-- Header -->
                <div style="border-bottom: 2px solid #f0f0f0; padding-bottom: 8px; margin-bottom: 12px;">
                  <div style="font-weight: bold; font-size: 16px; color: #1976d2; margin-bottom: 4px;">
                    ${product.product_code}
                  </div>
                  <div style="font-size: 13px; color: #666; line-height: 1.3; max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                    ${product.product_name}
                  </div>
                </div>

                <!-- Dados Financeiros -->
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 12px;">
                  <div style="background: #f8f9fa; padding: 8px; border-radius: 6px;">
                    <div style="font-size: 11px; color: #666; margin-bottom: 2px;">FATURAMENTO</div>
                    <div style="font-size: 14px; font-weight: bold; color: #1976d2;">
                      ${this.formatCurrencyLocale(product.total_value)}
                    </div>
                  </div>
                  <div style="background: #f8f9fa; padding: 8px; border-radius: 6px;">
                    <div style="font-size: 11px; color: #666; margin-bottom: 2px;">QUANTIDADE</div>
                    <div style="font-size: 14px; font-weight: bold; color: #1976d2;">
                      ${this.formatNumber(product.total_quantity)}
                    </div>
                  </div>
                </div>

                <!-- Análise de Lucro -->
                <div style="background: linear-gradient(135deg, ${profitColor}15, ${profitColor}05); padding: 10px; border-radius: 8px; border-left: 4px solid ${profitColor}; margin-bottom: 10px;">
                  <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px;">
                    <span style="font-size: 12px; font-weight: bold; color: #333;">Lucro REAL</span>
                    <span style="font-size: 18px; color: ${profitColor};">${profitIcon}</span>
                  </div>
                  <div style="font-size: 16px; font-weight: bold; color: ${profitColor}; margin-bottom: 4px;">
                    ${this.formatPercentage(
                      product.actual_total_profit_percent
                    )}
                  </div>
                  <div style="font-size: 11px; color: #666;">
                    vs ${this.formatPercentage(
                      product.variant_profit_percent
                    )} configurado
                    ${
                      profitDifference >= 0
                        ? `<span style="color: ${profitColor}; font-weight: bold;"> (+${this.formatPercentage(
                            profitDifference
                          )})</span>`
                        : `<span style="color: ${profitColor}; font-weight: bold;"> (${this.formatPercentage(
                            profitDifference
                          )})</span>`
                    }
                  </div>
                </div>

                <!-- Detalhes Adicionais -->
                <div style="display: flex; justify-content: space-between; font-size: 11px; color: #666;">
                  <div>
                    <strong>Preço Médio:</strong><br>
                    ${this.formatCurrencyLocale(product.average_total_value)}
                  </div>
                  <div style="text-align: right;">
                    <strong>Lucro Total:</strong><br>
                    ${this.formatCurrencyLocale(product.actual_total_profit)}
                  </div>
                </div>
              </div>
            `;
          },
        },
        colors: ["#FF4560"],
        grid: {
          borderColor: "#e7e7e7",
          row: {
            colors: ["#f3f3f3", "transparent"],
            opacity: 0.5,
          },
        },
      };
    },
  },

  mounted() {
    this.select(1);
  },

  methods: {
    // ===== MÉTODOS AUXILIARES =====
    hasValidData() {
      return this.products.data && this.products.data.length > 0;
    },

    formatNumber(value, decimals = 2) {
      return Number(value).toFixed(decimals);
    },

    formatCurrency(value, decimals = 2) {
      return `R$ ${this.formatNumber(value, decimals)}`;
    },

    formatCurrencyLocale(value) {
      return `R$ ${Number(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
      })}`;
    },

    formatPercentage(value, decimals = 2) {
      return `${this.formatNumber(value, decimals)}%`;
    },

    getGrandValue(field) {
      if (!this.hasValidData()) return 0;
      return Number(this.products.data[0]?.[field] || 0);
    },

    // ===== BUSCA E FILTROS =====
    async select(page = 1) {
      this.searchParams.page = page;
      this.isLoading = true;

      try {
        const response = await this.$http.index(
          "sale/sale-price-margin-analysis",
          this.searchParams
        );
        this.products = response;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    handleSearchInput(search) {
      this.searchParams.search = search ? search : null;
      this.select(1);
    },

    handleRelationshipInput(relationship, field) {
      if (relationship) {
        this.searchParams[field] = relationship.id;
      } else {
        this.searchParams[field] = null;
      }

      this.select(1);
    },

    // ===== CONTROLE DE COLUNAS =====
    toggleColumn(columnValue) {
      const index = this.visibleColumns.indexOf(columnValue);
      if (index > -1) {
        this.visibleColumns.splice(index, 1);
      } else {
        this.visibleColumns.push(columnValue);
      }
    },

    selectAllColumns() {
      this.visibleColumns = this.columnOptions.map((col) => col.value);
    },

    clearAllColumns() {
      this.visibleColumns = [];
    },

    resetDefaultColumns() {
      this.visibleColumns = [...this.defaultColumns];
    },

    // ===== EXPORTAÇÃO =====

    handleExport() {
      this.$http
        .download("sale/sale-price-margin-export", this.searchParams)
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `Analise_Precos_Margens_${this.searchParams.date_end}.xlsx`
          );
          document.body.appendChild(link);
          link.click();
          this.$loading.finish();
        })
        .catch((error) => {
          console.log(error);
          this.$loading.finish();
        });
    },
  },
};
</script>
